import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/useAuth';
import { AppShell } from './components/AppShell';
import { GlobalActivityIndicator } from './components/LoadingIndicators';
import { AssignmentsPage } from './features/assignments/AssignmentsPage';
import { AttendancePage } from './features/attendance/AttendancePage';
import { AuditLogPage } from './features/audit/AuditLogPage';
import { ActivitySessionsPage } from './features/activities/ActivitySessionsPage';
import { ActivitySitesPage } from './features/activitySites/ActivitySitesPage';
import { ProfilePage } from './features/account/ProfilePage';
import { LoginPage } from './features/auth/LoginPage';
import { InvitationAcceptancePage } from './features/auth/InvitationAcceptancePage';
import { BaRegistrationPage } from './features/auth/BaRegistrationPage';
import { AwaitingOrganizationPage } from './features/auth/AwaitingOrganizationPage';
import { ForgotPasswordPage, ResetPasswordPage } from './features/auth/PasswordRecoveryPages';
import { EmailVerificationPage } from './features/auth/EmailVerificationPage';
import { PrivacyNoticePage, TermsOfServicePage } from './features/legal/LegalPages';
import { CampaignsPage } from './features/campaigns/CampaignsPage';
import { CampaignSetupPage } from './features/campaigns/CampaignSetupPage';
import { CampaignReadOnlyPage } from './features/campaigns/CampaignReadOnlyPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { MembersPage } from './features/organizations/MembersPage';
import { OrganizationsPage } from './features/organizations/OrganizationsPage';
import { RelationshipsPage } from './features/organizations/RelationshipsPage';
import { ProductBrandsPage } from './features/products/ProductBrandsPage';
import { ProductCategoriesPage } from './features/products/ProductCategoriesPage';
import { ProductFamiliesPage } from './features/products/ProductFamiliesPage';
import { ProductsPage } from './features/products/ProductsPage';
import { PerformancePage } from './features/performance/PerformancePage';
import { SalesPage } from './features/sales/SalesPage';
import { ShelfEvidencePage } from './features/shelfEvidence/ShelfEvidencePage';
import { SurveysPage } from './features/surveys/SurveysPage';
import { SurveyTemplatesPage } from './features/surveys/SurveyTemplatesPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { StockPage } from './features/stock/StockPage';
import { SupervisionPage } from './features/supervision/SupervisionPage';
import { MerchandisingPage } from './features/merchandising/MerchandisingPage';
import { TalentPage } from './features/talent/TalentPage';
import { VenuesPage } from './features/venues/VenuesPage';
import { UsersPage } from './features/users/UsersPage';
import {
  BaAssignmentsPage,
  BaAttendancePage,
  BaHomePage,
  BaMobileShell,
  BaPerformancePage,
  BaSalesPage,
  BaShelfEvidencePage,
} from './features/ba/BaMobilePortal';
import {
  BaActivitiesPage,
  BaShelfStockPage,
  BaSurveysPage,
  MerchandiserFieldPage,
  SupervisorFieldPage,
} from './features/ba/FieldMobileExtras';
import type { Capability } from './types/domain';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
    mutations: { retry: 0 },
  },
});

export default function App() {
  const { accessToken, capabilities, loading, profile, selectedWorkspace } = useAuth();
  const location = useLocation();
  const isPublicAuthRoute = location.pathname.startsWith('/invite/')
    || location.pathname === '/forgot-password'
    || location.pathname === '/reset-password'
    || location.pathname === '/verify-email'
    || location.pathname.startsWith('/legal/');

  if (isPublicAuthRoute || !accessToken) {
    return (
      <QueryClientProvider client={queryClient}>
        <GlobalActivityIndicator />
        <Routes>
          <Route path="/invite/:token" element={<InvitationAcceptancePage />} />
          <Route path="/register" element={<BaRegistrationPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<EmailVerificationPage />} />
          <Route path="/legal/privacy" element={<PrivacyNoticePage />} />
          <Route path="/legal/terms" element={<TermsOfServicePage />} />
          <Route path="*" element={<LoginPage />} />
        </Routes>
      </QueryClientProvider>
    );
  }
  if (loading) return <div className="app-loader" aria-label="Loading"><span /></div>;

  const fieldRole = profile?.isBrandAmbassador && selectedWorkspace?.type === 'personal'
    ? 'brand_ambassador'
    : selectedWorkspace?.roles.includes('supervisor') && capabilities.includes('supervision:execute')
      ? 'supervisor'
      : selectedWorkspace?.roles.includes('merchandiser') && capabilities.includes('merchandising:execute')
        ? 'merchandiser'
        : null;
  if (fieldRole) {
    return (
      <QueryClientProvider client={queryClient}>
        <GlobalActivityIndicator />
        <BaMobileShell>
          {fieldRole === 'brand_ambassador' ? <Routes>
            <Route path="/" element={<BaHomePage />} />
            <Route path="/assignments" element={<BaAssignmentsPage />} />
            <Route path="/attendance" element={<BaAttendancePage />} />
            <Route path="/sales" element={<BaSalesPage />} />
            <Route path="/shelf-evidence" element={<BaShelfEvidencePage />} />
            <Route path="/shelf-stock" element={<BaShelfStockPage />} />
            <Route path="/surveys" element={<BaSurveysPage />} />
            <Route path="/activities" element={<BaActivitiesPage />} />
            <Route path="/performance" element={<BaPerformancePage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/talent" element={<TalentPage />} />
            <Route path="*" element={<Navigate replace to="/" />} />
          </Routes> : fieldRole === 'supervisor' ? <Routes>
            <Route path="/" element={<SupervisorFieldPage section="home" />} />
            <Route path="/stores" element={<SupervisorFieldPage section="stores" />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="*" element={<Navigate replace to="/" />} />
          </Routes> : <Routes>
            <Route path="/" element={<MerchandiserFieldPage section="home" />} />
            <Route path="/visits" element={<MerchandiserFieldPage section="visits" />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="*" element={<Navigate replace to="/" />} />
          </Routes>}
        </BaMobileShell>
      </QueryClientProvider>
    );
  }

  const allowed = (capability: Capability) => capabilities.includes(capability);

  return (
    <QueryClientProvider client={queryClient}>
      <GlobalActivityIndicator />
      <AppShell>
        <Routes>
          <Route path="/" element={allowed('overview:view') ? <DashboardPage /> : <AwaitingOrganizationPage />} />
          <Route path="/organisations" element={allowed('organizations:view') ? <OrganizationsPage /> : <Navigate replace to="/" />} />
          <Route path="/organisations/relationships" element={allowed('organizations:manage') ? <RelationshipsPage /> : <Navigate replace to="/" />} />
          <Route path="/organisations/:organizationId/members" element={allowed('organizations:manage') || allowed('merchandising:manage') ? <MembersPage /> : <Navigate replace to="/" />} />
          <Route path="/users" element={allowed('users:view') ? <UsersPage /> : <Navigate replace to="/" />} />
          <Route path="/talent" element={allowed('talent:view') ? <TalentPage /> : <Navigate replace to="/" />} />
          <Route path="/performance" element={allowed('performance:view') ? <PerformancePage /> : <Navigate replace to="/" />} />
          <Route path="/campaigns" element={allowed('campaigns:view') ? <CampaignsPage /> : <Navigate replace to="/" />} />
          <Route path="/campaigns/:campaignId/setup" element={allowed('campaigns:manage') ? <CampaignSetupPage /> : allowed('campaigns:view') ? <CampaignReadOnlyPage /> : <Navigate replace to="/" />} />
          <Route path="/venues" element={allowed('venues:view') ? <VenuesPage /> : <Navigate replace to="/" />} />
          <Route path="/activity-sites" element={allowed('venues:view') ? <ActivitySitesPage /> : <Navigate replace to="/" />} />
          <Route path="/assignments" element={allowed('assignments:view') ? <AssignmentsPage /> : <Navigate replace to="/" />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/attendance" element={allowed('attendance:view') || allowed('attendance:review') ? <AttendancePage /> : <Navigate replace to="/" />} />
          <Route path="/audit" element={allowed('audit:view') ? <AuditLogPage /> : <Navigate replace to="/" />} />
          <Route path="/products" element={allowed('products:view') ? <ProductsPage /> : <Navigate replace to="/" />} />
          <Route path="/product-brands" element={allowed('products:view') ? <ProductBrandsPage /> : <Navigate replace to="/" />} />
          <Route path="/product-families" element={allowed('products:view') ? <ProductFamiliesPage /> : <Navigate replace to="/" />} />
          <Route path="/product-categories" element={allowed('products:view') ? <ProductCategoriesPage /> : <Navigate replace to="/" />} />
          <Route path="/sales" element={allowed('sales:view') ? <SalesPage /> : <Navigate replace to="/" />} />
          <Route path="/shelf-evidence" element={allowed('shelf_evidence:view') ? <ShelfEvidencePage /> : <Navigate replace to="/" />} />
          <Route path="/surveys" element={allowed('surveys:view') ? <SurveysPage /> : <Navigate replace to="/" />} />
          <Route path="/activity-sessions" element={allowed('campaigns:view') ? <ActivitySessionsPage /> : <Navigate replace to="/" />} />
          <Route path="/survey-templates" element={allowed('surveys:manage') ? <SurveyTemplatesPage /> : <Navigate replace to="/surveys" />} />
          <Route path="/stock" element={allowed('stock:view') ? <StockPage /> : <Navigate replace to="/" />} />
          <Route path="/supervision" element={allowed('supervision:view') || allowed('supervision:manage') ? <SupervisionPage /> : <Navigate replace to="/" />} />
          <Route path="/merchandising" element={allowed('merchandising:view') || allowed('merchandising:manage') ? <MerchandisingPage /> : <Navigate replace to="/" />} />
          <Route path="/settings" element={allowed('settings:view') ? <SettingsPage /> : <Navigate replace to="/" />} />
          <Route path="*" element={<Navigate replace to="/" />} />
        </Routes>
      </AppShell>
    </QueryClientProvider>
  );
}
